import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { updateCompanyFunctions } from "./store/companiesPage"
import { updateTypesList } from "./store/projectsPage"
import httpClient from './components/helpers/httpClient'
import { BrowserRouter as Router, Route, Routes, useLocation } from "react-router-dom"
import 'react-toastify/dist/ReactToastify.css'
import { ToastContainer } from "react-toastify"
import { LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import LoginPage from "./pages/LoginPage"
import SignupPage from "./pages/SignupPage"
import DashboardPage from "./pages/DashboardPage"
import ContactsPage from "./pages/ContactsPage"
import ContactDetailPage from "./pages/ContactDetailPage"
import NewContactsPage from "./pages/NewContactsPage"
import CompaniesPage from "./pages/CompaniesPage"
import CompanyDetailPage from "./pages/CompanyDetailPage"
import ProductsPage from "./pages/ProductsPage"
import ProductDetailPage from "./pages/ProductDetailPage"
import ProjectsPage from "./pages/ProjectsPage"
import PropertiesPage from './pages/PropertiesPage'
import PropertyDetailPage from './pages/PropertyDetailPage'
import ProjectDetailPage from "./pages/ProjectDetailPage"
import TransactionsPage from "./pages/transactions/TransactionsPage"
import TransactionDetailPage from "./pages/transactions/TransactionDetailPage"
import CreateTransactionPage from "./pages/transactions/CreateTransactionPage"
import PurchaseOrderPDFPage from "./pages/purchase-orders/PurchaseOrderPDFPage"
import PurchaseOrdersPage from "./pages/purchase-orders/PurchaseOrdersPage"
import InvoicesPage from './pages/InvoicesPage'
import PurchaseOrderDetailPage from "./pages/purchase-orders/PurchaseOrderDetailPage"
import CreatePurchaseOrderPage from "./pages/purchase-orders/CreatePurchaseOrderPage"
import RentalContractsPage from "./pages/rental-contracts/RentalContractsPage"
import CreateRentalContractPage from "./pages/rental-contracts/CreateRentalContractPage"
import RentalContractDetailPage from "./pages/rental-contracts/RentalContractDetailPage"
import UnitDetailPage from "./pages/UnitDetailPage"
import SettingsPage from "./pages/SettingsPage"
import DuplicateReviewPage from './pages/DuplicateReviewPage'

import { useIsMobile } from './mobile/useIsMobile'
import MobileLoginPage from './mobile/MobileLoginPage'
import MobileDashboardPage from './mobile/MobileDashboardPage'
import MobileContactsPage from './mobile/contacts/MobileContactsPage'
import MobileContactDrawerRoute from './mobile/contacts/MobileContactDrawerRoute'
import MobileNewContactsPage from './mobile/contacts/MobileNewContactsPage'
import MobileCompaniesPage from './mobile/companies/MobileCompaniesPage'
import MobileCompanyDrawerRoute from './mobile/companies/MobileCompanyDrawerRoute'
import ForgotPasswordPage from './pages/ForgotPasswordPage'
import ResetPasswordPage from './pages/ResetPasswordPage'
import MobileForgotPasswordPage from './mobile/MobileForgotPasswordPage'
import MobileResetPasswordPage from './mobile/MobileResetPasswordPage'
import ProtectedRoute from './components/ProtectedRoute'
import './mobile/mobile.css'

function AppRoutes() {
  const isMobile = useIsMobile();
  const location = useLocation();
  const state = location.state;

  return (
    <>
      <Routes location={state?.backgroundLocation || location}>
        {/* Public routes */}
        <Route path="/" element={isMobile ? <MobileLoginPage /> : <LoginPage />} />
        <Route path="/signup" element={<SignupPage />} />
        <Route path="/forgotpassword" element={isMobile ? <MobileForgotPasswordPage /> : <ForgotPasswordPage />} />
        <Route path="/reset-password" element={isMobile ? <MobileResetPasswordPage /> : <ResetPasswordPage />} />
        
        {/* Protected routes */}
        <Route path="/dashboard" element={<ProtectedRoute>{isMobile ? <MobileDashboardPage /> : <DashboardPage />}</ProtectedRoute>} />
        
        {/* Contacts */}
        <Route 
          path="/contacts" 
          element={<ProtectedRoute>{isMobile ? <MobileContactsPage /> : <ContactsPage />}</ProtectedRoute>} 
        />
        <Route 
          path="/contacts/new" 
          element={<ProtectedRoute>{isMobile ? <MobileNewContactsPage /> : <NewContactsPage />}</ProtectedRoute>} 
        />
        <Route path="/contacts/duplicates" element={<ProtectedRoute><DuplicateReviewPage entityType="contact" /></ProtectedRoute>} />
        <Route 
          path="/contacts/:contactID" 
          element={<ProtectedRoute>{isMobile ? <MobileContactsPage /> : <ContactDetailPage />}</ProtectedRoute>} 
        />

        {/* Companies */}
        <Route 
          path="/companies" 
          element={<ProtectedRoute>{isMobile ? <MobileCompaniesPage /> : <CompaniesPage />}</ProtectedRoute>} 
        />
        <Route path="/companies/duplicates" element={<ProtectedRoute><DuplicateReviewPage entityType="company" /></ProtectedRoute>} />
        <Route 
          path="/companies/:companyID" 
          element={<ProtectedRoute>{isMobile ? <MobileCompaniesPage /> : <CompanyDetailPage />}</ProtectedRoute>} 
        />

        <Route path="/products" element={<ProtectedRoute><ProductsPage /></ProtectedRoute>} />
        <Route path="/products/:productID" element={<ProtectedRoute><ProductDetailPage /></ProtectedRoute>} />
        <Route path="/projects" element={<ProtectedRoute><ProjectsPage /></ProtectedRoute>} />
        <Route path="/properties" element={<ProtectedRoute><PropertiesPage /></ProtectedRoute>} />
        <Route path="/properties/detail" element={<ProtectedRoute><PropertyDetailPage /></ProtectedRoute>} />
        <Route path="/projects/:projectID" element={<ProtectedRoute><ProjectDetailPage /></ProtectedRoute>} />
        <Route path="/projects/:projectID/buildings/:buildingID/units/:unitID" element={<ProtectedRoute><UnitDetailPage /></ProtectedRoute>} />
        <Route path="/transactions" element={<ProtectedRoute><TransactionsPage /></ProtectedRoute>} />
        <Route path="/transactions/new" element={<ProtectedRoute><CreateTransactionPage /></ProtectedRoute>} />
        <Route path="/transactions/:transactionId" element={<ProtectedRoute><TransactionDetailPage /></ProtectedRoute>} />
        <Route path="/purchase-orders/:poId/pdf" element={<ProtectedRoute><PurchaseOrderPDFPage /></ProtectedRoute>} />
        <Route path="/purchase-orders/new" element={<ProtectedRoute><CreatePurchaseOrderPage /></ProtectedRoute>} />
        <Route path="/purchase-orders" element={<ProtectedRoute><PurchaseOrdersPage /></ProtectedRoute>} />
        <Route path="/invoices" element={<ProtectedRoute><InvoicesPage /></ProtectedRoute>} />
        <Route path="/purchase-orders/:poId" element={<ProtectedRoute><PurchaseOrderDetailPage /></ProtectedRoute>} />
        <Route path="/rental-contracts" element={<ProtectedRoute><RentalContractsPage /></ProtectedRoute>} />
        <Route path="/rental-contracts/new" element={<ProtectedRoute><CreateRentalContractPage /></ProtectedRoute>} />
        <Route path="/rental-contracts/:contractId" element={<ProtectedRoute><RentalContractDetailPage /></ProtectedRoute>} />
        <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
      </Routes>

      {/* Mobile Drawer Overlay Routes */}
      {isMobile && state?.backgroundLocation && (
        <Routes>
          <Route path="/contacts/:contactID" element={<MobileContactDrawerRoute />} />
          <Route path="/companies/:companyID" element={<MobileCompanyDrawerRoute />} />
        </Routes>
      )}
    </>
  );
}

function App() {
  const dispatch = useDispatch()
  const companyFunctions = useSelector(state => state.companiesPage.companyFunctions)
  const typesList = useSelector(state => state.projectsPage.typesList)

  useEffect(() => {
    console.log("App mounted")
    initializeAppState()
  }, [])

  const initializeAppState = async () => {
    await Promise.all([
      initializeCompanyFunctions(),
      initializeTypesList(),
    ])
  }

  const initializeCompanyFunctions = async () => {
    if (!companyFunctions || companyFunctions.length === 0) {
      try {
        const response = await httpClient.get(import.meta.env.VITE_API_URL + "/api/company/functions")
        if (response.status === 200) {
          dispatch(updateCompanyFunctions(response.data))
        } else {
          console.error("Error fetching company functions:", response.status)
        } 
      } catch (error) {
        console.error("Error fetching company functions:", error)
      }
    }
  }

  const initializeTypesList = async () => {
    if (!typesList) {
      try {
        const response = await httpClient.get(import.meta.env.VITE_API_URL + "/api/projects/types")
        if (response.status === 200) {
          dispatch(updateTypesList(response.data))
        } else {
          console.error("Error fetching types:", response.status)
        }
      } catch (error) {
        console.error("Error fetching types:", error)
      }
    }
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Router>
        <AppRoutes />

        <div className="App">
          <ToastContainer position="bottom-left" theme="colored" />
        </div>
      </Router>
    </LocalizationProvider >
  )
}

export default App
